import { useState, useCallback, useRef } from 'react';
import { swapChainsAndCompact, buildPositionsForChainSwap } from './utils/autoLayoutUtils';
import { clearCampaignTidyManaged } from './flowchartLayout/tidyManagedCampaigns';

// Animation timing constants
const NODE_ANIMATION_DURATION = 300; // CSS transition duration for nodes
const EDGE_FADE_IN_DURATION = 250;   // CSS animation duration for edge fade-in

/**
 * useChainReorder Hook
 * 
 * Handles chain reordering with smooth animations.
 * Swaps chains visually using current positions + tetris compaction,
 * then updates metadata (order) via the original handlers.
 * 
 * Animation sequence:
 * 1. Edges hidden instantly (CSS: opacity: 0)
 * 2. Nodes slide to new positions (CSS transition: 300ms)
 * 3. Edges fade in after nodes finish (CSS animation: 250ms)
 */
export function useChainReorder({
  tasks,
  interaction,
  nodeDimensions,
  displayPositions,
  campaignId,
  onPositionChangeBatch,
  onChainMoveLeft,
  onChainMoveRight,
}) {
  // Track animation state for smooth node transitions during chain reorder
  const [isAnimatingReorder, setIsAnimatingReorder] = useState(false);
  const animationTimeoutRef = useRef(null);
  const edgeFadeTimeoutRef = useRef(null);

  // Helper to trigger animated reorder
  const triggerAnimatedReorder = useCallback((newPositions, batchDescription, onComplete) => {
    // Clear any pending timeouts
    if (animationTimeoutRef.current) {
      clearTimeout(animationTimeoutRef.current);
    }
    if (edgeFadeTimeoutRef.current) {
      clearTimeout(edgeFadeTimeoutRef.current);
    }
    
    // Manual chain rearrange — stop tidy-managed snap from overwriting the swap
    // when ELK re-layouts after chainOrder metadata updates.
    clearCampaignTidyManaged(campaignId);

    // Enable animation class BEFORE updating positions
    // This hides edges instantly via CSS
    setIsAnimatingReorder(true);
    
    // Use requestAnimationFrame to ensure the class is applied before position change
    requestAnimationFrame(() => {
      // Update visual positions (will animate due to CSS transition)
      if (interaction.setCustomPositions) {
        interaction.setCustomPositions(newPositions);
      }
      
      // Save positions to task data
      if (onPositionChangeBatch) {
        const positionChanges = [];
        tasks.forEach(task => {
          const pos = newPositions[task.id];
          if (pos) {
            positionChanges.push({
              taskId: task.id,
              position: pos,
              oldPosition: task.x !== undefined && task.y !== undefined 
                ? { x: task.x, y: task.y } 
                : null,
            });
          }
        });
        if (positionChanges.length > 0) {
          onPositionChangeBatch(positionChanges, { batchType: 'CHAIN_REORDER', batchDescription });
        }
      }
      
      // Call completion callback (e.g., update chain metadata)
      onComplete?.();
      
      // After nodes finish animating, trigger edge fade-in
      animationTimeoutRef.current = setTimeout(() => {
        // Remove the animating class - this allows edges to become visible
        setIsAnimatingReorder(false);
        
        // Add fade-in class to all edges
        const edges = document.querySelectorAll('.flowchart__edge');
        edges.forEach(edge => edge.classList.add('flowchart__edge--reorder-fade-in'));
        
        // Remove fade-in class after animation completes
        edgeFadeTimeoutRef.current = setTimeout(() => {
          edges.forEach(edge => edge.classList.remove('flowchart__edge--reorder-fade-in'));
        }, EDGE_FADE_IN_DURATION + 50);
      }, NODE_ANIMATION_DURATION + 50); // Small buffer after node animation
    });
  }, [interaction, tasks, onPositionChangeBatch, campaignId]);

  const runChainSwap = useCallback((chainId, chainInfo, direction, onComplete) => {
    // Match on-screen coords (custom → display merge → saved), not sparse custom alone
    const currentPositions = buildPositionsForChainSwap(
      tasks,
      interaction.customPositionsRef?.current,
      displayPositions,
    );

    const newPositions = swapChainsAndCompact(
      currentPositions,
      tasks,
      nodeDimensions,
      chainId,
      direction,
      chainInfo
    );

    // Abort was a no-op (same reference) — don't update metadata / animate
    if (!newPositions || newPositions === currentPositions) {
      return;
    }

    const batchDescription = direction < 0 ? 'Move chain left' : 'Move chain right';
    triggerAnimatedReorder(newPositions, batchDescription, onComplete);
  }, [tasks, interaction, nodeDimensions, displayPositions, triggerAnimatedReorder]);

  const handleChainMoveLeftWithSwap = useCallback((chainId, chainInfo) => {
    runChainSwap(chainId, chainInfo, -1, () => {
      onChainMoveLeft?.(chainId, chainInfo);
    });
  }, [runChainSwap, onChainMoveLeft]);

  const handleChainMoveRightWithSwap = useCallback((chainId, chainInfo) => {
    runChainSwap(chainId, chainInfo, 1, () => {
      onChainMoveRight?.(chainId, chainInfo);
    });
  }, [runChainSwap, onChainMoveRight]);

  return {
    isAnimatingReorder,
    handleChainMoveLeftWithSwap,
    handleChainMoveRightWithSwap,
  };
}

export default useChainReorder;
